/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

'use client';

import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import mergeRefs from 'merge-refs';
import invariant from 'tiny-invariant';
import warning from 'warning';
import * as pdfjs from 'pdfjs-dist';

import StructTree from './StructTree';

import PageContext from './PageContext';
import {
    cancelRunningTask,
    getDevicePixelRatio,
    isCancelException,
    makePageCallback,
} from './shared/utils';

import type { RenderParameters } from 'pdfjs-dist/types/src/display/api.js';
import { Box, CircularProgress } from '@mui/material';

const ANNOTATION_MODE = pdfjs.AnnotationMode;

type CanvasProps = {
    canvasRef?: React.Ref<HTMLCanvasElement>;
};

export default function Canvas(props: CanvasProps): React.ReactElement {
    const pageContext = useContext(PageContext);

    invariant(pageContext, 'Unable to find Page context.');

    const mergedProps = { ...pageContext, ...props };
    const {
        _className,
        canvasBackground,
        devicePixelRatio = getDevicePixelRatio(),
        onRenderError: onRenderErrorProps,
        onRenderSuccess: onRenderSuccessProps,
        page,
        renderForms,
        renderTextLayer,
        rotate,
        scale,
    } = mergedProps;
    const { canvasRef } = props;

    invariant(
        page,
        'Attempted to render page canvas, but no page was specified.'
    );
    const [isWASM, setWASM] = useState<boolean>(true);
    const [isRendering, setRendering] = useState<boolean>(false);
    const [isCanvasWASM, setCanvasWASM] = useState<boolean>(false);

    const canvasElement = useRef<HTMLCanvasElement>(null);

    /**
     * Called when a page is rendered successfully.
     */
    function onRenderSuccess() {
        if (!page) {
            // Impossible, but TypeScript doesn't know that
            return;
        }

        if (onRenderSuccessProps) {
            onRenderSuccessProps(makePageCallback(page, scale));
        }
    }

    /**
     * Called when a page fails to render.
     */
    function onRenderError(error: Error) {
        if (isCancelException(error)) {
            return;
        }

        warning(false, error.toString());

        if (onRenderErrorProps) {
            onRenderErrorProps(error);
        }
    }

    const renderViewport = useMemo(
        () =>
            page.getViewport({
                scale: scale * devicePixelRatio,
                rotation: rotate,
            }),
        [devicePixelRatio, page, rotate, scale]
    );

    const viewport = useMemo(
        () => page.getViewport({ scale, rotation: rotate }),
        [page, rotate, scale]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Ommitted callbacks so they are not called every time they change
    useEffect(() => {
        if (!page) {
            return;
        }

        // Ensures the canvas will be re-rendered from scratch. Otherwise all form data will stay.
        page.cleanup();

        const { current: canvas } = canvasElement;

        if (!canvas) {
            return;
        }
        setRendering(false);
        canvas.width = renderViewport.width;
        canvas.height = renderViewport.height;

        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.style.visibility = 'hidden';

        const renderContext: RenderParameters = {
            annotationMode: renderForms
                ? ANNOTATION_MODE.ENABLE_FORMS
                : ANNOTATION_MODE.ENABLE,
            canvasContext: canvas.getContext('2d', {
                alpha: false,
            }) as CanvasRenderingContext2D,
            viewport: renderViewport,
        };
        if (canvasBackground) {
            renderContext.background = canvasBackground;
        }

        const cancellable = page.render(renderContext);
        const runningTask = cancellable;

        cancellable.promise
            .then(() => {
                canvas.style.visibility = '';
                setRendering(true);
                onRenderSuccess();
            })
            .catch(onRenderError);

        return () => cancelRunningTask(runningTask);
    }, [canvasBackground, page, renderForms, renderViewport, viewport]);

    const cleanup = useCallback(() => {
        const { current: canvas } = canvasElement;

        /**
         * Zeroing the width and height cause most browsers to release graphics
         * resources immediately, which can greatly reduce memory consumption.
         */
        if (canvas) {
            canvas.width = 0;
            canvas.height = 0;
        }
    }, []);

    useEffect(() => cleanup, [cleanup]);

    const handleWASMFetch = useCallback(() => {
        setWASM(true);
    }, []);
    useEffect(() => {
        // eslint-disable-next-line no-underscore-dangle
        if (page && page._transport) {
            // eslint-disable-next-line no-underscore-dangle

            setWASM(false);
            // eslint-disable-next-line no-underscore-dangle
            const transporter = page._transport;
            if (transporter.wasmFactory) {
                const wasmTarget: EventTarget =
                    transporter.wasmFactory.baseTarget;

                wasmTarget.addEventListener('fetch', handleWASMFetch);
            }
        }
    }, [page]);
    useEffect(() => {
        // eslint-disable-next-line no-underscore-dangle
        if (page && page._transport) {
            // eslint-disable-next-line no-underscore-dangle

            // eslint-disable-next-line no-underscore-dangle
            const transporter = page._transport;
            if (transporter.wasmFactory && transporter.wasmFactory.baseWasm) {
                setCanvasWASM(true);
            } else {
                setCanvasWASM(false);
            }
        } else {
            setCanvasWASM(false);
        }
    }, [isWASM, page]);

    return (
        <Box position="relative">
            <canvas
                className={`${_className}__canvas`}
                dir="ltr"
                ref={mergeRefs(canvasRef, canvasElement)}
                style={{
                    display: 'block',
                    userSelect: 'none',
                }}
            >
                {renderTextLayer ? <StructTree /> : null}
            </canvas>
            {isCanvasWASM && isRendering === false && (
                <Box
                    position="absolute"
                    width={viewport.width}
                    height={viewport.height}
                    top={0}
                >
                    <Box className="align-loader">
                        <CircularProgress
                            className="mr-2 margin-loader"
                            size={54}
                            color="inherit"
                            title="progress loading"
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
}
